import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FiImage, FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/SEO';

const Gallery = () => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const lightboxRef = useRef(null);
  const imageRefs = useRef([]);

  const images = [
    { url: "/img/gallery/img_4.jpeg", title: "Learning Together", aspectRatio: 1.5 },
    { url: "/img/gallery/img_5.jpeg", title: "Creative Arts", aspectRatio: 1.2 },
    { url: "/img/gallery/img_7.jpeg", title: "Outdoor Play", aspectRatio: 0.8 },
    { url: "/img/gallery/img_8.jpeg", title: "Sensory Learning", aspectRatio: 1.3 },
    { url: "/img/gallery/img_9.jpeg", title: "Drawing Class", aspectRatio: 1.1 },
    { url: "/img/gallery/img_10.jpeg", title: "STEM Projects", aspectRatio: 1.4 },
    { url: "/img/gallery/img_11.jpeg", title: "Celebrations", aspectRatio: 0.9 },
    { url: "/img/gallery/img_13.jpeg", title: "Circle Time", aspectRatio: 1.6 },
    { url: "/img/gallery/img_14.jpeg", title: "Group Activities", aspectRatio: 1.0 },
    { url: "/img/gallery/img_17.jpeg", title: "Music & Movement", aspectRatio: 1.7 },
    { url: "/img/gallery/img_18.jpeg", title: "Story Time", aspectRatio: 0.7 },
    { url: "/img/gallery/img_21.jpeg", title: "Playground Fun", aspectRatio: 1.2 }
  ];

  // Lazy loading with Intersection Observer
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '50px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = parseInt(entry.target.dataset.index);
          setLoadedImages(prev => new Set([...prev, index]));
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    imageRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Handle image load
  const handleImageLoad = useCallback((index) => {
    setLoadedImages(prev => new Set([...prev, index]));
  }, []);

  // Lightbox functions
  const openLightbox = useCallback((index) => {
    setSelectedImageIndex(index);
    setIsLightboxOpen(true);
    // Prevent body scroll and layout shift
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    // Hide header
    document.body.classList.add('lightbox-open');
  }, []);

  const closeLightbox = useCallback(() => {
    setIsLightboxOpen(false);
    setSelectedImageIndex(null);
    // Restore body scroll
    document.body.style.overflow = 'unset';
    document.body.style.paddingRight = '0px';
    // Show header
    document.body.classList.remove('lightbox-open');
  }, []);

  const navigateImage = useCallback((direction) => {
    if (selectedImageIndex === null) return;

    const newIndex = selectedImageIndex + direction;
    if (newIndex >= 0 && newIndex < images.length) {
      setSelectedImageIndex(newIndex);
    }
  }, [selectedImageIndex, images.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isLightboxOpen) return;

      switch (e.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          navigateImage(-1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          navigateImage(1);
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, closeLightbox, navigateImage]);

  // Click outside to close
  const handleBackdropClick = useCallback((e) => {
    if (e.target === lightboxRef.current) {
      closeLightbox();
    }
  }, [closeLightbox]);

  // Lightbox modal component using React Portal
  const LightboxModal = useCallback(() => {
    if (!isLightboxOpen || selectedImageIndex === null) return null;

    return createPortal(
      <>
        {/* Premium Backdrop with subtle blur and vignette */}
        <div className="fixed inset-0 bg-black/95 backdrop-blur-[2px] z-40" style={{
          background: 'radial-gradient(circle at center, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.95) 100%)'
        }} />

        {/* Modal Wrapper */}
        <div
          ref={lightboxRef}
          className="fixed inset-0 z-[200] flex items-center justify-center p-8"
          onClick={handleBackdropClick}
        >
          {/* Close Button - Top Right, Modern & Subtle */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 z-[210] w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 flex items-center justify-center text-white transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/30 opacity-70 hover:opacity-100"
            aria-label="Close gallery"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>

          {/* Navigation Arrows - Premium Circular Design */}
          {selectedImageIndex > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); navigateImage(-1); }}
              className="absolute z-[210] w-12 h-12 rounded-full bg-white/8 hover:bg-white/15 backdrop-blur-md border border-white/15 flex items-center justify-center text-white transition-all duration-500 hover:scale-105 hover:shadow-md hover:shadow-white/5 focus:outline-none focus:ring-2 focus:ring-white/20 opacity-50 hover:opacity-80 group
                         md:left-8 md:top-1/2 md:-translate-y-1/2
                         left-20 bottom-8 md:bottom-auto"
              aria-label="Previous image"
            >
              <SafeIcon icon={FiChevronLeft} className="w-6 h-6 group-hover:scale-105 transition-transform duration-300" />
            </button>
          )}

          {selectedImageIndex < images.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); navigateImage(1); }}
              className="absolute z-[210] w-12 h-12 rounded-full bg-white/8 hover:bg-white/15 backdrop-blur-md border border-white/15 flex items-center justify-center text-white transition-all duration-500 hover:scale-105 hover:shadow-md hover:shadow-white/5 focus:outline-none focus:ring-2 focus:ring-white/20 opacity-50 hover:opacity-80 group
                         md:right-8 md:top-1/2 md:-translate-y-1/2
                         right-20 bottom-8 md:bottom-auto"
              aria-label="Next image"
            >
              <SafeIcon icon={FiChevronRight} className="w-6 h-6 group-hover:scale-105 transition-transform duration-300" />
            </button>
          )}

          {/* Image Counter - Bottom Center, Elegant */}
          <div className="absolute z-[210] bg-black/40 backdrop-blur-md text-white px-6 py-3 rounded-full text-sm font-medium border border-white/10 opacity-80
                         md:bottom-8 md:left-1/2 md:-translate-x-1/2
                         bottom-8 left-1/2 -translate-x-1/2">
            {selectedImageIndex + 1} / {images.length}
          </div>

          {/* Main Image - Perfectly Centered with Elegant Spacing */}
          <div className="relative max-w-[90vw] max-h-[85vh] flex items-center justify-center">
            <img
              src={images[selectedImageIndex].url}
              alt={images[selectedImageIndex].title}
              className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg shadow-2xl"
              style={{
                maxWidth: 'min(90vw, 1600px)',
                maxHeight: 'min(85vh, 1000px)'
              }}
            />
          </div>
        </div>
      </>,
      document.getElementById('modal-root')
    );
  }, [isLightboxOpen, selectedImageIndex, handleBackdropClick, closeLightbox, navigateImage, images]);

  return (
    <div className="animate-fadeIn">
      <SEO page="gallery" />

      {/* Hero Section with Background */}
      <section className="relative bg-gradient-to-br from-pink-900 via-rose-800 to-red-900 pt-32 pb-40 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 border-4 border-white rounded-full"></div>
          <div className="absolute top-20 right-20 w-20 h-20 border-4 border-white"></div>
          <div className="absolute bottom-10 left-1/4 w-24 h-24 border-4 border-white rounded-full"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Breadcrumbs */}
          <div className="flex items-center justify-center space-x-2 text-sm text-pink-200 mb-6" data-aos="fade-down">
            <a href="/" className="hover:text-white transition-colors">Home</a>
            <span className="text-pink-400">/</span>
            <span className="text-white font-medium">Gallery</span>
          </div>

          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6" data-aos="fade-up">
              <SafeIcon icon={FiImage} className="w-5 h-5 text-white" />
              <span className="text-white font-medium">Photo Gallery</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6" data-aos="fade-up" data-aos-delay="100">
              Moments of Joy
            </h1>
            
            <p className="text-xl text-pink-100 leading-relaxed" data-aos="fade-up" data-aos-delay="200">
              Explore our vibrant environment where learning meets fun every day. 
              Witness the joy, creativity, and wonder that fills our classrooms.
            </p>
          </div>
        </div>

        {/* Decorative Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" className="w-full h-auto">
            <path fill="#ffffff" fillOpacity="1" d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,58.7C960,64,1056,64,1152,58.7C1248,53,1344,43,1392,37.3L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
          </svg>
        </div>
      </section>

      {/* Masonry Gallery */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-6 space-y-6">
            {images.map((img, idx) => (
              <div
                key={idx}
                ref={el => imageRefs.current[idx] = el}
                data-index={idx}
                className="break-inside-avoid cursor-pointer group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                onClick={() => openLightbox(idx)}
                data-aos="fade-up"
                data-aos-delay={idx * 50}
              >
                <div className="relative overflow-hidden rounded-2xl">
                  {loadedImages.has(idx) ? (
                    <img
                      src={img.url}
                      alt={img.title}
                      className="w-full h-auto object-cover transition-all duration-700 group-hover:scale-110 opacity-100"
                      loading="lazy"
                      onLoad={() => handleImageLoad(idx)}
                    />
                  ) : (
                    <div className="w-full bg-gray-200 animate-pulse rounded-2xl" style={{ aspectRatio: img.aspectRatio }}>
                      <div className="flex items-center justify-center h-full">
                        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    </div>
                  )}

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-white font-bold text-lg leading-tight">{img.title}</h3>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal Portal */}
      <LightboxModal />
    </div>
  );
};

export default Gallery;